import {
  getAllImporters,
  updateImporter,
  createImporter,
  deleteImporter,
} from 'common/src/api/importers';
import { Importer } from 'common/src/utils/types';
import React, { useEffect, useState } from 'react';
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Button,
  useDisclosure,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Image,
  Card,
  CardBody,
  Skeleton,
} from '@nextui-org/react';
import { toast } from 'react-toastify';
import Header from '../../../components/layouts/Header';
import { ConfirmModal, PageLayout } from '../../../components/ui';
import { Icon } from '@iconify/react';

const SuperadminImporters = () => {
  const [importers, setImporters] = useState<Importer[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedImporter, setSelectedImporter] = useState<Importer | null>(
    null
  );
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [newImporter, setNewImporter] = useState<{
    name: string;
    excel_name: string;
    logo_url: string;
  }>({
    name: '',
    excel_name: '',
    logo_url: '',
  });
  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const {
    isOpen: isNewOpen,
    onOpen: onNewOpen,
    onClose: onNewClose,
  } = useDisclosure();

  const fetchImporters = async () => {
    setLoading(true);
    try {
      const fetchedImporters = await getAllImporters();
      setImporters(fetchedImporters);
    } catch (error) {
      console.error('Error fetching importers:', error);
      toast.error('Error al cargar importadores');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchImporters();
  }, []);

  const handleEdit = (importer: Importer) => {
    setSelectedImporter(importer);
    onEditOpen();
  };

  const handleDelete = (importer: Importer) => {
    setSelectedImporter(importer);
    onDeleteOpen();
  };

  const handleEditSubmit = async () => {
    if (!selectedImporter) return;
    try {
      await updateImporter(selectedImporter.id, {
        name: selectedImporter.name,
        excel_name: selectedImporter.excel_name,
        logo_url: selectedImporter.logo_url,
      });
      toast.success('Importador actualizado correctamente');
      onEditClose();
      fetchImporters();
    } catch (error) {
      console.error('Error updating importer:', error);
      toast.error('Error al actualizar importador');
    }
  };

  const handleNewSubmit = async () => {
    if (!newImporter.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    try {
      await createImporter(newImporter);
      toast.success('Importador creado correctamente');
      onNewClose();
      setNewImporter({ name: '', excel_name: '', logo_url: '' });
      fetchImporters();
    } catch (error) {
      console.error('Error creating importer:', error);
      toast.error('Error al crear importador');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedImporter) return;
    try {
      await deleteImporter(selectedImporter.id);
      toast.success('Importador eliminado correctamente');
      onDeleteClose();
      fetchImporters();
    } catch (error) {
      console.error('Error deleting importer:', error);
      toast.error('Error al eliminar importador');
    }
  };

  const columns = [
    { name: 'ID', uid: 'id' },
    { name: 'Logo', uid: 'logo' },
    { name: 'Nombre', uid: 'name' },
    { name: 'Nombre Excel', uid: 'excel_name' },
    { name: 'Acciones', uid: 'actions' },
  ];

  const renderCell = (importer: Importer, columnKey: React.Key) => {
    switch (columnKey) {
      case 'logo':
        return importer.logo_url ? (
          <Image
            src={importer.logo_url}
            alt={`${importer.name} logo`}
            width={40}
            height={40}
            className='rounded-full'
          />
        ) : (
          <div className='w-10 h-10 rounded-full bg-default-200 flex items-center justify-center'>
            <span className='text-default-500 text-xs'>N/A</span>
          </div>
        );
      case 'actions':
        return (
          <div className='flex items-center gap-2'>
            <Button
              isIconOnly
              size='sm'
              variant='light'
              onClick={() => handleEdit(importer)}
            >
              <Icon icon='tabler:edit' className='text-default-400' />
            </Button>
            <Button
              isIconOnly
              size='sm'
              variant='light'
              color='danger'
              onClick={() => handleDelete(importer)}
            >
              <Icon icon='tabler:trash' className='text-danger-400' />
            </Button>
          </div>
        );
      default:
        return String(importer[columnKey as keyof Importer] || '');
    }
  };

  const filteredImporters = importers
    .filter((importer) => {
      const searchLower = searchQuery.toLowerCase();
      return (
        !searchQuery ||
        importer.name?.toLowerCase().includes(searchLower) ||
        importer.excel_name?.toLowerCase().includes(searchLower)
      );
    })
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  return (
    <PageLayout>
      <Header
        title='Importadores'
        actions={
          <Button color='primary' onClick={onNewOpen}>
            Añadir Importador
          </Button>
        }
      />
      <div className='mb-4'>
        <Input
          placeholder='Buscar por nombre...'
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className='max-w-xl'
          isClearable
          onClear={() => setSearchQuery('')}
        />
      </div>

      {loading ? (
        <div className=''>
          <div className='flex justify-between items-center'>
            <div className='space-y-2'>
              <Skeleton className='w-1/3 rounded-lg' />
            </div>
          </div>

          <Card className='w-full'>
            <CardBody>
              <div className='space-y-4'>
                <div className='grid grid-cols-4 gap-4'>
                  {[...Array(4)].map((_, i) => (
                    <Skeleton key={i} className='h-8 w-full rounded-lg' />
                  ))}
                </div>

                {[...Array(5)].map((_, rowIndex) => (
                  <div key={rowIndex} className='grid grid-cols-4 gap-4'>
                    <Skeleton className='h-12 w-16 rounded-lg' />
                    <Skeleton className='h-12 w-12 rounded-full' />
                    <Skeleton className='h-12 w-full rounded-lg' />
                    <div className='flex gap-2 justify-center'>
                      <Skeleton className='h-8 w-8 rounded-lg' />
                      <Skeleton className='h-8 w-8 rounded-lg' />
                    </div>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>
        </div>
      ) : (
        <Table removeWrapper aria-label='Importers table'>
          <TableHeader columns={columns}>
            {(column) => (
              <TableColumn key={column.uid}>{column.name}</TableColumn>
            )}
          </TableHeader>
          <TableBody items={filteredImporters}>
            {(item) => (
              <TableRow key={item.id}>
                {(columnKey) => (
                  <TableCell>{renderCell(item, columnKey)}</TableCell>
                )}
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      {/* Edit Modal */}
      <Modal isOpen={isEditOpen} onClose={onEditClose} size='lg'>
        <ModalContent>
          <ModalHeader>Editar Importador</ModalHeader>
          <ModalBody>
            <Input
              label='Nombre'
              value={selectedImporter?.name || ''}
              onChange={(e) =>
                setSelectedImporter({
                  ...selectedImporter!,
                  name: e.target.value,
                })
              }
            />
            <Input
              label='Nombre en Excel'
              value={selectedImporter?.excel_name || ''}
              onChange={(e) =>
                setSelectedImporter({
                  ...selectedImporter!,
                  excel_name: e.target.value,
                })
              }
            />
            <Input
              label='URL del Logo'
              value={selectedImporter?.logo_url || ''}
              onChange={(e) =>
                setSelectedImporter({
                  ...selectedImporter!,
                  logo_url: e.target.value,
                })
              }
            />
            {selectedImporter?.logo_url && (
              <div className='mt-2'>
                <p className='text-sm text-default-500 mb-2'>Vista previa:</p>
                <Image
                  src={selectedImporter.logo_url}
                  alt='Logo preview'
                  width={80}
                  height={80}
                  className='rounded-lg'
                />
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              variant='light'
              onClick={onEditClose}
            >
              Cancelar
            </Button>
            <Button
              color='primary'
              onClick={handleEditSubmit}
            >
              Guardar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Modal */}
      <ConfirmModal
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        onConfirm={handleDeleteConfirm}
        title='Confirmar Eliminación'
        confirmLabel='Eliminar'
        cancelLabel='Cancelar'
        isDestructive
      >
        ¿Estás seguro de que deseas eliminar a {selectedImporter?.name}?
      </ConfirmModal>

      {/* New Modal */}
      <Modal isOpen={isNewOpen} onClose={onNewClose} size='lg'>
        <ModalContent>
          <ModalHeader>Nuevo Importador</ModalHeader>
          <ModalBody>
            <Input
              label='Nombre'
              value={newImporter.name}
              onChange={(e) =>
                setNewImporter({ ...newImporter, name: e.target.value })
              }
              isRequired
            />
            <Input
              label='Nombre en Excel'
              value={newImporter.excel_name}
              onChange={(e) =>
                setNewImporter({ ...newImporter, excel_name: e.target.value })
              }
            />
            <Input
              label='URL del Logo'
              value={newImporter.logo_url}
              onChange={(e) =>
                setNewImporter({ ...newImporter, logo_url: e.target.value })
              }
            />
            {newImporter.logo_url && (
              <div className='mt-2'>
                <p className='text-sm text-default-500 mb-2'>Vista previa:</p>
                <Image
                  src={newImporter.logo_url}
                  alt='Logo preview'
                  width={80}
                  height={80}
                  className='rounded-lg'
                />
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              variant='light'
              onClick={onNewClose}
            >
              Cancelar
            </Button>
            <Button
              color='primary'
              onClick={handleNewSubmit}
            >
              Crear
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </PageLayout>
  );
};

export default SuperadminImporters;
